import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquareWarning, X, ClipboardList, GitBranch } from "lucide-react";
import { LABSYSO_FEEDBACK } from "../../config/labsysoFeedback";
import LabsysoComplaintsFlowchart from "./LabsysoComplaintsFlowchart";
import LabsysoFeedbackForm from "./LabsysoFeedbackForm";

const TABS = [
  { id: "formulario", label: "Enviar solicitud", icon: ClipboardList },
  { id: "proceso", label: "Proceso y plazos", icon: GitBranch },
];

/**
 * Botón flotante + panel del buzón de quejas LABSYSO (solo en /labsyso).
 */
export default function LabsysoFeedbackWidget({
  open,
  onOpenChange,
  logoUrl,
  socialMedia = {},
}) {
  const [activeTab, setActiveTab] = useState("formulario");
  const isControlled = typeof open === "boolean";
  const [internalOpen, setInternalOpen] = useState(false);
  const panelOpen = isControlled ? open : internalOpen;

  const setOpen = (value) => {
    if (!isControlled) setInternalOpen(value);
    onOpenChange?.(value);
  };

  useEffect(() => {
    if (!panelOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [panelOpen]);

  return (
    <>
      {/* Botón flotante */}
      <motion.button
        type="button"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.6, type: "spring", stiffness: 260, damping: 18 }}
        onClick={() => {
          setActiveTab("formulario");
          setOpen(true);
        }}
        className="fixed z-[980] right-4 md:right-6 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom,0px)+16px)] md:bottom-8 flex items-center gap-3 group"
        aria-label="Abrir buzón de quejas, reclamos y sugerencias LABSYSO"
      >
        <span className="hidden md:flex items-center gap-2 bg-[#221d52] text-white text-xs font-bold uppercase tracking-widest px-4 py-3 rounded-full shadow-xl opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
          Quejas y sugerencias
        </span>
        <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-[#221d52] text-[#f5b419] shadow-[0_8px_30px_rgba(34,29,82,0.45)] ring-4 ring-[#f5b419]/25 hover:scale-105 active:scale-95 transition-transform">
          <MessageSquareWarning size={26} strokeWidth={2.2} />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#f5b419] border-2 border-white" />
        </span>
      </motion.button>

      <AnimatePresence>
        {panelOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2100] bg-[#221d52]/85 backdrop-blur-md flex items-end md:items-center justify-center p-0 md:p-6"
            onClick={() => setOpen(false)}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={LABSYSO_FEEDBACK.title}
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              className="bg-[#fafcfb] w-full md:max-w-5xl max-h-[min(92vh,900px)] md:rounded-[32px] shadow-2xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <header className="shrink-0 bg-[#221d52] text-white px-5 md:px-8 py-5 md:py-6 border-b border-[#f5b419]/20">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    {logoUrl && (
                      <img
                        src={logoUrl}
                        alt="LABSYSO"
                        className="h-10 md:h-12 w-auto object-contain bg-white/95 rounded-xl px-2 py-1 shrink-0"
                      />
                    )}
                    <div className="min-w-0">
                      <p className="text-[#f5b419] text-[10px] font-bold uppercase tracking-[0.25em] mb-1">
                        {LABSYSO_FEEDBACK.formCode}
                      </p>
                      <h2 className="font-display font-black text-lg md:text-2xl leading-tight">
                        {LABSYSO_FEEDBACK.title}
                      </h2>
                      <p className="text-white/75 text-xs md:text-sm mt-2 leading-relaxed hidden sm:block">
                        {LABSYSO_FEEDBACK.subtitle}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="shrink-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    aria-label="Cerrar buzón"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="flex gap-2 mt-5 p-1 bg-white/10 rounded-2xl w-full md:w-fit">
                  {TABS.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActiveTab(id)}
                      className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                        activeTab === id
                          ? "bg-[#f5b419] text-[#221d52] shadow-md"
                          : "text-white/80 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      <Icon size={15} />
                      {label}
                    </button>
                  ))}
                </div>
              </header>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-4 md:p-6">
                {activeTab === "formulario" ? (
                  <div id="labsyso-feedback-form" className="max-w-3xl mx-auto">
                    <LabsysoFeedbackForm />
                  </div>
                ) : (
                  <LabsysoComplaintsFlowchart
                    socialMedia={socialMedia}
                    onOpenForm={() => setActiveTab("formulario")}
                  />
                )}
              </div>

              <footer className="shrink-0 px-5 py-3 border-t border-[#221d52]/10 bg-white text-center text-[10px] text-gray-500 font-medium">
                Laboratorio Clínico LABSYSO · Procedimiento de calidad · Documentos F-PGC-6-1 / F-PGC-6-2
              </footer>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
